import React, { useEffect, useState, useMemo, useRef } from 'react';
import { observer } from 'mobx-react';
import { Divider, Form, message, Popconfirm, Skeleton, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import { debounce, find } from 'lodash';
import { useNavigate, useParams } from 'react-router-dom';
import parsePhoneNumber from 'libphonenumber-js';
import { MinusSquareOutlined } from '@ant-design/icons';
import moment from 'moment';

import { EPInput, EPInputSearch, SeverityIcon, EPTableWrapper, EPCheckbox } from '@/comp';
import { useStores } from '@/hooks/use-stores';
import { dialCodes, permissions } from '@/constants';
import InsightIcon from '@/assets/insight.svg?react';
import LeakIcon from '@/assets/leak.svg?react';
import { DeleteOutlined } from '@ant-design/icons';
import { User } from '@/store/UsersStore';
import { browserLanguage } from '@/i18n/i18n';
import { EPButton, EPSelect, EPSelectOption } from '@/components';
import EPSwitch from '@/comp/EPSwitch';

import {
  Container,
  StationTableContainer,
  ButtonContainer,
  ActionsContainer,
  ColumnHeaderContent,
  StyledSkeleton,
} from './UserCreateOrEditForm.styles';
import {
  isDisableValueDashboardReportByRow,
  isValueDashboardChecked,
  hasUnselectPermission,
  hasUserUpdatedAnyField,
  FormValues,
  MODES,
  stationNameMatchesSearchTerm,
  prepareNotificationSubscriptions,
  FORM_DEFAULT_INITIAL_VALUES,
  isAddStationButtonDisabled,
  isRoleSelectDisabledByRowIndex,
  getStationName,
  getSkeletonProps,
  getMappedUserFavoriteStations,
  getMappedUserStations,
} from './UserCreateOrEditForm.utils';
import ValueDashboardReportRow from './UserFormComponents/ValueDashboardReportRow';
import ExtendedNotificationRows from './UserFormComponents/ExtendedNotificationRows';
import StationSelectCell from './UserFormComponents/StationSelectCell';
import RoleSelectCell from './UserFormComponents/RoleSelectCell';
import NotificationPreferenceRow from './UserFormComponents/NotificationPreferenceRow';
import RemoveAccessButton from './UserFormComponents/RemoveAccessButton';
import AddStationButtonRow from './UserFormComponents/AddStationButtonRow';
import { AlertLevels } from '@/constants/enums';
import FavouriteStartCell from './UserFormComponents/FavouriteStartCell';

interface DeleteUserProps {
  onDelete: () => Promise<void>;
  onCancel: () => void;
  loading: boolean;
  isFormDisabled: boolean;
  t: (key: string) => string;
  mode: string;
  isEditingSelfUser: boolean;
  isReady: boolean;
}

const DeleteUser: React.FC<DeleteUserProps> = ({
  onDelete,
  onCancel,
  loading,
  isFormDisabled,
  t,
  mode,
  isEditingSelfUser,
  isReady,
}) => {
  if (mode === MODES.EDIT && !isEditingSelfUser) {
    return (
      <Form.Item>
        <Popconfirm
          placement={'top'}
          okText={'Delete user'}
          cancelText={'Cancel'}
          title={t('You are about to delete this user completely. Are you sure?')}
          onConfirm={onDelete}
        >
          <EPButton
            showSkeleton={!isReady}
            loading={loading}
            disabled={isFormDisabled}
            type="link"
            icon={<DeleteOutlined />}
          >
            {t('Delete user')}
          </EPButton>
        </Popconfirm>
      </Form.Item>
    );
  }

  if (mode === MODES.CREATE) {
    return (
      <Form.Item>
        <EPButton showSkeleton={!isReady} disabled={isFormDisabled} onClick={onCancel}>
          {t('Cancel')}
        </EPButton>
      </Form.Item>
    );
  }

  return null;
};

const UserCreateOrEditForm = observer(
  ({ user, onChangeDirty, isReady }: { user: User; onChangeDirty: (boolean) => void; isReady: boolean }) => {
    const { userStore, usersStore, associableEntitiesStore, stationStore, permissionStore } = useStores();
    const navigate = useNavigate();
    const params = useParams();
    const { t, i18n } = useTranslation();
    const [form] = Form.useForm();
    const [mode] = useState(user ? MODES.EDIT : MODES.CREATE);
    const [submitting, setSubmitting] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [, setTime] = useState<Date>();
    const [initialValues, setInitialValues] = useState<FormValues>();
    const [isDirty, setIsDirty] = useState(false);
    const [valueDashboardStation, setValueDashboardStation] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const isFormDisabled = submitting || deleting;
    const isExternalUser = user?.isExternalUser;
    const isEditingSelfUser = user?.id && user.id === userStore.user?.id;
    const periodValueDashboard = ['ValueDashboardDaily', 'ValueDashboardWeekly', 'ValueDashboardReportMonthly'];
    const lastUpdatedLabel = user?.lastUpdated ? moment(user.lastUpdated).format('MMMM D, YYYY') : null;
    const addStationRef = useRef(null);
    const skeletonProps = getSkeletonProps(isReady);

    const stationLookupMap = useMemo(() => {
      const map = {};

      if (associableEntitiesStore.stations) {
        associableEntitiesStore.stations.forEach(station => {
          map[station.id] = station;
        });
      }

      const allStationIds =
        form
          .getFieldValue('stationAndSubscriptions')
          ?.map?.(s => s?.stationId)
          .filter(Boolean) || [];
      allStationIds.forEach(stationId => {
        if (!map[stationId]) {
          const station = stationStore.getStationById(stationId);
          if (station) {
            map[stationId] = station;
          }
        }
      });

      return map;
    }, [associableEntitiesStore.stations, form, stationStore]);

    useEffect(() => {
      const fetchStationValueDashboard = async () => {
        const data = await usersStore.fetchStationValueDashboard();
        setValueDashboardStation(data);
      };

      fetchStationValueDashboard();
    }, []);

    useEffect(() => {
      onChangeDirty && onChangeDirty(isDirty);
    }, [isDirty]);

    useEffect(() => {
      if (isReady) {
        if (!user) {
          setInitialValues(FORM_DEFAULT_INITIAL_VALUES);
          form.setFieldsValue(FORM_DEFAULT_INITIAL_VALUES);
        }
        if (user) {
          const newUserData = user.formData?.stationAndSubscriptions.map(stationSubscribe => {
            if (stationSubscribe?.subscriptions) {
              const existingField = periodValueDashboard.find(fieldName =>
                Object.prototype.hasOwnProperty.call(stationSubscribe?.subscriptions, fieldName),
              );
              if (existingField) {
                const newSub = {
                  ...stationSubscribe.subscriptions,
                  ValueDashboardReport: {
                    status: existingField,
                    email: stationSubscribe?.subscriptions[existingField]?.email,
                    sms: stationSubscribe?.subscriptions[existingField]?.sms,
                  },
                };
                delete newSub[existingField];
                stationSubscribe.subscriptions = newSub;
              }
            }
            return stationSubscribe;
          });
          if (newUserData) {
            const newUser = {
              firstName: user?.formData?.firstName,
              cc: user?.formData?.cc,
              email: user?.formData?.email,
              lastName: user?.formData?.lastName,
              notificationsEnabled: user?.formData?.notificationsEnabled,
              measurementSystem: user?.formData.measurementSystem,
              number: user?.formData?.number,
              stationAndSubscriptions: newUserData,
              language: user?.formData?.language,
            };
            setInitialValues(newUser);
            form.setFieldsValue(newUser);
          }
        }
      }
    }, [user, isReady]);

    const unselect = field => {
      const stationAndSubscriptions = form.getFieldValue('stationAndSubscriptions');
      if (stationAndSubscriptions[field.key].subscriptions) {
        delete stationAndSubscriptions[field.key].subscriptions;
        setIsDirty(true);
        form.setFieldValue('stationAndSubscriptions', stationAndSubscriptions);
      }
    };

    const onFinish = values => {
      const userStations = getMappedUserStations(values.stationAndSubscriptions);
      const notificationSubscriptions = prepareNotificationSubscriptions(values.stationAndSubscriptions);
      const dialCode = find(dialCodes, { isoCode2digits: values.cc });

      const payload = {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        phoneNumber: {
          countryCode: values.cc,
          dialCode: dialCode.code,
          number: values.number,
        },
        measurementSystem: values.measurementSystem,
        userStations,
        userFavoriteStations: getMappedUserFavoriteStations(values.stationAndSubscriptions),
        notificationSubscriptions,
        notificationsEnabled: values.notificationsEnabled,
        language: values.language,
      };

      if (mode === MODES.EDIT) onUpdate(payload);
      if (mode === MODES.CREATE) onCreate(payload);
    };

    const onFinishExternalUser = values => {
      const userStations = getMappedUserStations(values.stationAndSubscriptions);
      const notificationSubscriptions = prepareNotificationSubscriptions(values.stationAndSubscriptions);

      const payload = {
        measurementSystem: values.measurementSystem,
        userStations,
        notificationSubscriptions,
        notificationsEnabled: values.notificationsEnabled,
        language: values.language,
        userFavoriteStations: getMappedUserFavoriteStations(values.stationAndSubscriptions),
      };

      if (mode === MODES.EDIT) onUpdate(payload);
    };

    const onUpdate = async payload => {
      try {
        setSubmitting(true);
        isEditingSelfUser && userStore.setLanguage(payload?.language);

        const hasUpdated = hasUserUpdatedAnyField(initialValues, payload);
        if (hasUpdated) {
          await usersStore.update(user.id, payload);
          localStorage.setItem('language', payload?.language);
          await i18n.changeLanguage(payload?.language);
          navigate(`/home/station/${params.stationId}/settings/users`);
        } else {
          message.info(t('Nothing to update. No changes were made to the information'));
        }
      } finally {
        setSubmitting(false);
      }
    };

    const onCreate = async payload => {
      try {
        setSubmitting(true);
        localStorage.setItem('language', payload?.language);
        await i18n.changeLanguage(payload?.language);
        await usersStore.create(payload);
        navigate(`/home/station/${params.stationId}/settings/users`);
      } catch (e) {
        setSubmitting(false);
      }
    };

    const onDelete = async () => {
      try {
        setDeleting(true);
        await usersStore.delete(user.id);
        navigate(`/home/station/${params.stationId}/settings/users`);
      } catch (e) {
        setDeleting(false);
      }
    };

    const onCancel = () => {
      navigate(`/home/station/${params.stationId}/settings/users`);
    };

    const onValuesChange = () => {
      setIsDirty(true);
    };

    const onChangeSearchInput = e => {
      setSearchTerm(e.target.value.trim());
    };

    const handleAddStation = () => {
      if (addStationRef.current) {
        addStationRef.current(
          {
            subscriptions: {
              ShutdownNotification: {
                email: true,
                sms: true,
              },
            },
          },
          0,
        );
      }
    };
    return (
      <Container className="user-form-responsive-container">
        <>
          <header>
            <div>
              <Skeleton
                {...skeletonProps}
                paragraph={{ rows: 1, width: 270 }}
                rootClassName="user-form-header-skeleton"
              >
                <h1>{mode === MODES.EDIT ? user.fullName : t('Create New User')}</h1>
              </Skeleton>
              <Skeleton {...skeletonProps} title={{ width: 270 }} paragraph={false}>
                {mode === MODES.EDIT && (
                  <div className="last-updated-label">
                    {t('Last updated on:')}
                    {lastUpdatedLabel}
                  </div>
                )}
              </Skeleton>
            </div>
            {mode === MODES.EDIT && (
              <DeleteUser
                onDelete={onDelete}
                onCancel={onCancel}
                loading={deleting}
                isFormDisabled={isFormDisabled}
                t={t}
                mode={mode}
                isEditingSelfUser={isEditingSelfUser}
                isReady={isReady}
              />
            )}
          </header>
          <Form
            form={form}
            layout={'vertical'}
            initialValues={initialValues}
            onValuesChange={onValuesChange}
            onFinish={isExternalUser ? onFinishExternalUser : onFinish}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
              }
            }}
          >
            <header>
              <StyledSkeleton {...skeletonProps}>
                <Form.Item
                  label={t('First name')}
                  name="firstName"
                  rules={[
                    {
                      required: !isExternalUser,
                      message: t('Please input your first name'),
                    },
                  ]}
                >
                  <EPInput placeholder={t('E.g. John')} disabled={isFormDisabled || isExternalUser} />
                </Form.Item>
              </StyledSkeleton>
              <StyledSkeleton {...skeletonProps}>
                <Form.Item
                  label={t('Last name')}
                  name="lastName"
                  rules={[
                    {
                      required: !isExternalUser,
                      message: t('Please input your last name'),
                    },
                  ]}
                >
                  <EPInput placeholder={t('E.g. Doe')} disabled={isFormDisabled || isExternalUser} />
                </Form.Item>
              </StyledSkeleton>
              <StyledSkeleton {...skeletonProps}>
                <Space.Compact>
                  <Form.Item
                    label={t('Country Code')}
                    name="cc"
                    rules={[
                      {
                        required: !isExternalUser,
                        message: t('Please input your dial code'),
                      },
                    ]}
                  >
                    <EPSelect
                      placeholder={t('+001')}
                      dropdownMatchSelectWidth={false}
                      labelInValue={false}
                      showSearch
                      filterOption={(inputValue, option) => {
                        const optionValue = option.children.toLowerCase();
                        const optionKey = option.key.toLowerCase();
                        const inputValueLowered = inputValue.toLowerCase();
                        return optionValue.includes(inputValueLowered) || optionKey.includes(inputValueLowered);
                      }}
                      disabled={isFormDisabled || isExternalUser}
                      onChange={() => form.validateFields(['number'])}
                    >
                      {dialCodes.map(dialCode => (
                        <EPSelectOption value={dialCode.isoCode2digits} key={dialCode.isoCode2digits}>
                          {`${dialCode.name} (+${dialCode.code})`}
                        </EPSelectOption>
                      ))}
                    </EPSelect>
                  </Form.Item>

                  <Form.Item
                    label={'Phone Number'}
                    name="number"
                    rules={[
                      {
                        required: !isExternalUser,
                        message: t('Please input your number'),
                      },
                      {
                        min: 9,
                        max: 13,
                        message: t('9 to 13 digits are allowed'),
                      },
                      {
                        pattern: /^\d+$/,
                        message: t('Only numbers are allowed!'),
                      },
                      {
                        validator: (_, value) => {
                          if (isExternalUser) {
                            return Promise.resolve();
                          }

                          const countryCode = form.getFieldValue('cc');
                          const phoneNumber = parsePhoneNumber(value, countryCode);

                          return phoneNumber?.isValid()
                            ? Promise.resolve()
                            : Promise.reject(`Not a valid ${countryCode} number`);
                        },
                      },
                    ]}
                  >
                    <EPInput
                      placeholder={t('000000000')}
                      style={{ width: 140 }}
                      disabled={isFormDisabled || isExternalUser}
                    />
                  </Form.Item>
                </Space.Compact>
              </StyledSkeleton>
              <StyledSkeleton {...skeletonProps}>
                <Form.Item
                  label={t('Email')}
                  name="email"
                  normalize={value => value?.toLowerCase().trim() ?? value}
                  rules={[
                    {
                      required: true,
                      message: t('Please input your email'),
                    },
                    {
                      type: 'email',
                      message: t('Please input a valid email address'),
                    },
                  ]}
                >
                  <EPInput
                    placeholder={t('John@domain.com')}
                    {...{ disabled: mode === MODES.EDIT }}
                    disabled={mode === MODES.EDIT || isFormDisabled}
                  />
                </Form.Item>
              </StyledSkeleton>
              <StyledSkeleton {...skeletonProps}>
                <Form.Item
                  label={t('Measurement system')}
                  name="measurementSystem"
                  rules={[
                    {
                      required: true,
                      message: t('Please choose your measurement system'),
                    },
                  ]}
                >
                  <EPSelect
                    placeholder={t('Select measurement system')}
                    dropdownMatchSelectWidth={false}
                    disabled={isFormDisabled}
                  >
                    <EPSelectOption value="metric">{t('Metric')}</EPSelectOption>
                    <EPSelectOption value="imperial">{t('Imperial')}</EPSelectOption>
                  </EPSelect>
                </Form.Item>
              </StyledSkeleton>

              <StyledSkeleton {...skeletonProps}>
                <Form.Item label={t('language')} name="language">
                  <EPSelect
                    placeholder={t('selectLanguage')}
                    defaultValue={localStorage.getItem('language') || browserLanguage}
                    dropdownMatchSelectWidth={false}
                    disabled={isFormDisabled}
                  >
                    <EPSelectOption value="en">{t('english')}</EPSelectOption>
                    <EPSelectOption value="fr">{t('french')}</EPSelectOption>
                    <EPSelectOption value="es">{t('spanish')}</EPSelectOption>
                    <EPSelectOption value="pt">{t('portuguese')}</EPSelectOption>
                    <EPSelectOption value="de">{t('german')}</EPSelectOption>
                  </EPSelect>
                </Form.Item>
              </StyledSkeleton>
              <StyledSkeleton {...skeletonProps}>
                {permissionStore.isAuth(permissions.UPDATE_USER_NOTIFICATIONS_ENABLED) && (
                  <Form.Item label={t('Notifications')} name="notificationsEnabled" className="centered-switch">
                    <EPSwitch loading={submitting} disabled={isFormDisabled} onLabel={t('On')} offLabel={t('Off')} />
                  </Form.Item>
                )}
              </StyledSkeleton>
            </header>

            <Divider className={'horizontal-line'} />
            <ButtonContainer>
              <h2 style={{ marginBottom: '0' }}>{t('userAssociatedStations') + ` (${user?.stations?.length || 0})`}</h2>
              <ActionsContainer>
                <EPInputSearch
                  className="search-station-input"
                  placeholder={t('searchStation')}
                  value={searchTerm}
                  onChange={debounce(onChangeSearchInput, 500)}
                />
                <AddStationButtonRow
                  onAddStation={handleAddStation}
                  disabled={isAddStationButtonDisabled(form, associableEntitiesStore) || isFormDisabled}
                  isReady={isReady}
                />
                <EPButton type="primary" htmlType="submit" loading={submitting} showSkeleton={!isReady}>
                  {t('saveChanges')}
                </EPButton>
              </ActionsContainer>
            </ButtonContainer>

            <div className="user-stations-container">
              <Form.List name="stationAndSubscriptions">
                {(fields, { add, remove }) => {
                  if (addStationRef.current === null) {
                    addStationRef.current = add;
                  }

                  const dataSource = fields.filter(field => {
                    const stationData = form.getFieldValue('stationAndSubscriptions')?.[field.name];
                    if (!searchTerm) return true;
                    return stationNameMatchesSearchTerm(stationStore, stationData, searchTerm);
                  });

                  const columns = [
                    ...(isEditingSelfUser
                      ? [
                          {
                            dataIndex: 'isFavorite',
                            key: 'isFavorite',
                            width: '1%',
                            render: (_, __, index) => {
                              if (!dataSource[index]) return null;

                              return (
                                <FavouriteStartCell
                                  field={dataSource[index]}
                                  form={form}
                                  disabled={isFormDisabled || usersStore.isUpdating}
                                />
                              );
                            },
                          },
                        ]
                      : []),
                    {
                      title: <ColumnHeaderContent>{t('Station')}</ColumnHeaderContent>,
                      dataIndex: 'station',
                      key: 'station',
                      width: '15%',
                      render: (_, __, index) => {
                        if (!dataSource[index]) return null;

                        return (
                          <StationSelectCell
                            field={dataSource[index]}
                            index={index}
                            form={form}
                            associableEntitiesStore={associableEntitiesStore}
                            isDisabled={!associableEntitiesStore.isReady || isFormDisabled || isEditingSelfUser}
                            t={t}
                            setTime={setTime}
                            stationLookupMap={stationLookupMap}
                            getStationName={stationId => getStationName(stationId, stationLookupMap)}
                          />
                        );
                      },
                    },
                    {
                      title: <ColumnHeaderContent>{t('Role')}</ColumnHeaderContent>,
                      dataIndex: 'role',
                      key: 'role',
                      width: '15%',
                      render: (_, __, index) => {
                        if (!dataSource[index]) return null;

                        return (
                          <RoleSelectCell
                            field={dataSource[index]}
                            index={index}
                            form={form}
                            isDisabled={
                              isRoleSelectDisabledByRowIndex(index, form) || isFormDisabled || isEditingSelfUser
                            }
                            t={t}
                          />
                        );
                      },
                    },
                    {
                      title: <ColumnHeaderContent>{t('Notifications')}</ColumnHeaderContent>,
                      dataIndex: 'notifications',
                      key: 'notifications',
                      width: '60%',
                      render: (_, __, index) => {
                        if (!dataSource[index]) return null;

                        const field = dataSource[index];
                        return (
                          <>
                            <NotificationPreferenceRow
                              field={field}
                              severity={{ icon: <SeverityIcon severity={AlertLevels.Shutdown} /> }}
                              label="Shutdown"
                              description="Manufacturer and Custom Alerts with Shutdown Severity"
                              keyPrefix="ShutdownNotification"
                              isFormDisabled={isFormDisabled}
                              t={t}
                              showSms={true}
                            />
                            <NotificationPreferenceRow
                              field={field}
                              severity={{ icon: <SeverityIcon severity={AlertLevels.Warning} /> }}
                              label="Warning"
                              description="Manufacturer and Custom Alerts with Warning Severity"
                              keyPrefix="WarningNotification"
                              isFormDisabled={isFormDisabled}
                              t={t}
                              showSms={true}
                            />
                            <NotificationPreferenceRow
                              field={field}
                              severity={{ icon: <SeverityIcon severity={AlertLevels.Service} /> }}
                              label="Service"
                              description="Manufacturer and Custom Alerts with Service Severity"
                              keyPrefix="ServiceNotification"
                              isFormDisabled={isFormDisabled}
                              t={t}
                              showSms={true}
                            />

                            {permissionStore.isAuth(permissions.EXTENDED_NOTIFICATION_ALERTS_SUBSCRIPTION) && (
                              <>
                                <NotificationPreferenceRow
                                  field={field}
                                  severity={{ icon: <LeakIcon /> }}
                                  label="leakMeasurement"
                                  description="Notifies on Periodic Shutdown event creation, 1 day before the event"
                                  keyPrefix="LeakMeasurement"
                                  isFormDisabled={isFormDisabled}
                                  t={t}
                                  showSms={false}
                                />
                                <NotificationPreferenceRow
                                  field={field}
                                  severity={{ icon: <InsightIcon /> }}
                                  label="Insight"
                                  description="Notifies when a new Insight Event is created"
                                  keyPrefix="Insight"
                                  isFormDisabled={isFormDisabled}
                                  t={t}
                                  showSms={false}
                                />
                                <ExtendedNotificationRows field={field} isFormDisabled={isFormDisabled} />
                                <ValueDashboardReportRow
                                  field={field}
                                  isFormDisabled={isFormDisabled}
                                  isValueDashboardChecked={isValueDashboardChecked(index, form)}
                                  isDisableValueDashboardReport={isDisableValueDashboardReportByRow(
                                    index,
                                    form,
                                    valueDashboardStation,
                                  )}
                                  onTimeChange={() => setTime(new Date())}
                                />
                                <div className="notification-settings-row-container">
                                  <div className="severity-container">
                                    <LeakIcon /> <span className="label">{t('Leakage')}</span>
                                  </div>
                                  <div className="notification-description-container">
                                    {t('leakageCreatedNotificationMessage')}
                                  </div>
                                  <div className="notification-preference-container">
                                    <Form.Item
                                      {...field}
                                      name={[field.name, 'subscriptions', 'Leakage', 'email']}
                                      valuePropName={'checked'}
                                    >
                                      <EPCheckbox disabled={isFormDisabled}>{t('Email')}</EPCheckbox>
                                    </Form.Item>
                                  </div>
                                </div>
                              </>
                            )}

                            {hasUnselectPermission(field.name, form, userStore, stationStore) && (
                              <div className="notification-settings-row-container">
                                <div className="severity-container"></div>
                                <div className="notification-description-container"></div>
                                <div className="notification-preference-container">
                                  <div className="unselect-container" onClick={() => unselect(field)}>
                                    <MinusSquareOutlined />
                                    {t('Unselect')}
                                  </div>
                                </div>
                              </div>
                            )}
                          </>
                        );
                      },
                    },
                    {
                      title: '  ',
                      key: 'action',
                      width: '10%',
                      render: (_, __, index) => {
                        if (!dataSource[index]) return null;

                        return (
                          <RemoveAccessButton
                            field={dataSource[index]}
                            remove={remove}
                            form={form}
                            t={t}
                            isFormDisabled={isFormDisabled}
                            isEditingSelfUser={isEditingSelfUser}
                          />
                        );
                      },
                    },
                  ];

                  return (
                    <>
                      <StationTableContainer>
                        <EPTableWrapper
                          sticky={true}
                          scroll={{ x: false }}
                          useCustomScroll={true}
                          dataSource={dataSource}
                          columns={columns}
                          rowKey="key"
                          pagination={false}
                          className="user-stations-table"
                          initialRowCount={searchTerm ? dataSource?.length : 3}
                          rowIncrementCount={3}
                          viewportHeightMultiplier={0.5}
                          showAllRowsButton={true}
                          showScrollButtons={true}
                          loading={!isReady || (!searchTerm && dataSource?.length === 0)}
                          loadingRowCount={6}
                        />
                      </StationTableContainer>
                    </>
                  );
                }}
              </Form.List>
            </div>
          </Form>
        </>
      </Container>
    );
  },
);

export default UserCreateOrEditForm;
